import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { isEmptyNode } from '@/utils/react-util'
import { Button } from '@/components/Button'

export interface ConfirmSheetProps {
  children?: ReactNode
  title?: ReactNode
  yes?: string
  no?: string
  danger?: boolean
  disableCloseOnEsc?: boolean
  disableCloseOnOutsideClick?: boolean
  showing: boolean
  /**
   * Whether the confirmed action is underway, which keeps the sheet open.
   */
  isPending?: boolean
  onConfirm: () => void
  onDecline: () => void
  confirmButtonTestId?: string
  declineButtonTestId?: string
  closeButtonTestId?: string
}

/**
 * Asks the user to confirm an action, in a modal sheet.
 */
export function ConfirmSheet({
  children,
  title,
  yes = 'Confirm',
  no = 'Cancel',
  danger,
  disableCloseOnOutsideClick,
  disableCloseOnEsc,
  showing,
  isPending,
  onConfirm,
  onDecline,
  confirmButtonTestId,
  declineButtonTestId,
  closeButtonTestId,
}: ConfirmSheetProps) {
  const hasTitle = !isEmptyNode(title)
  return (
    <ModalSheet
      disableCloseOnOutsideClick={disableCloseOnOutsideClick}
      disableCloseOnEsc={disableCloseOnEsc}
      showing={showing}
      onRequestClose={isPending ? undefined : onDecline}
    >
      <ModalSheetHeader
        withCloseButton
        closeButtonTestId={closeButtonTestId}
        bordered={hasTitle}
      >
        {hasTitle && <ModalSheetTitle>{title}</ModalSheetTitle>}
      </ModalSheetHeader>
      <ModalSheetContent>{children}</ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label={no}
            testId={declineButtonTestId}
            isDisabled={isPending}
            onPress={onDecline}
          />
          <Button
            label={yes}
            testId={confirmButtonTestId}
            variant={danger ? 'destructive' : 'primary'}
            isPending={isPending}
            onPress={onConfirm}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
